import { useEffect, useState } from "react";
import type {
  CustomerAdjustmentItem,
  CustomerConsentItem,
  CustomerRedemptionItem,
  CustomerTransactionItem,
  TenantCustomerAdjustmentsResponse,
  TenantCustomerConsentsResponse,
  TenantCustomerRedemptionsResponse,
  TenantCustomerTransactionsResponse,
} from "@orbit/api-zod";
import { merchantFetch } from "../lib/merchantAuth";

/**
 * Customer 360 sub-tab feeds — Phase 13.
 *
 * Each tab is a standalone, paginated list backed by the matching
 * `/api/v1/tenant/customers/:tcId/<feed>` endpoint. We use a tiny
 * shared paginated-state hook (cursor stack → newer/older buttons)
 * rather than TanStack Query because the data is owner-scoped and
 * already lives behind the parent page's tcId; cache invalidation
 * across tabs would just pull every tab on every visit anyway.
 *
 * REVOKED / ANONYMIZED handling:
 *   • All historical data is preserved — voids, refunds, vouchers,
 *     adjustments, consent_log entries — so a merchant who needs
 *     audit context after the customer disconnects still gets it.
 *   • The parent page renders the banner + masks the header; these
 *     tabs render their data without status-aware tweaks (the data
 *     itself is the value).
 */

function usePaginated<T>(
  url: string | null,
  pageSize: number,
): {
  items: T[];
  loading: boolean;
  err: string | null;
  hasNewer: boolean;
  hasOlder: boolean;
  newer: () => void;
  older: () => void;
} {
  const [stack, setStack] = useState<Array<string | null>>([null]);
  const [items, setItems] = useState<T[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    void (async () => {
      const cursor = stack[stack.length - 1];
      const sp = new URLSearchParams();
      sp.set("limit", String(pageSize));
      if (cursor) sp.set("cursor", cursor);
      const sep = url.includes("?") ? "&" : "?";
      const r = await merchantFetch<{ items: T[]; nextCursor: string | null }>(
        `${url}${sep}${sp.toString()}`,
      );
      if (cancelled) return;
      setLoading(false);
      if (!r.ok) {
        setErr(`${r.code}: ${r.message}`);
        setItems([]);
        setNextCursor(null);
        return;
      }
      setItems(r.data.items);
      setNextCursor(r.data.nextCursor);
    })();
    return () => {
      cancelled = true;
    };
  }, [url, stack, pageSize]);

  return {
    items,
    loading,
    err,
    hasNewer: stack.length > 1,
    hasOlder: !!nextCursor,
    newer: () => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s)),
    older: () =>
      nextCursor && setStack((s) => [...s, nextCursor]),
  };
}

function PaginationControls({
  hasNewer,
  hasOlder,
  newer,
  older,
}: {
  hasNewer: boolean;
  hasOlder: boolean;
  newer: () => void;
  older: () => void;
}) {
  if (!hasNewer && !hasOlder) return null;
  return (
    <div className="mt-4 flex justify-between">
      <button
        type="button"
        disabled={!hasNewer}
        onClick={newer}
        className="btn-secondary disabled:opacity-50"
      >
        Newer
      </button>
      <button
        type="button"
        disabled={!hasOlder}
        onClick={older}
        className="btn-secondary disabled:opacity-50"
      >
        Older
      </button>
    </div>
  );
}

// ----- 1. Transactions tab -----

export function CustomerTransactionsTab({ tcId }: { tcId: string }) {
  const url = `/api/v1/tenant/customers/${tcId}/transactions`;
  const p = usePaginated<CustomerTransactionItem>(url, 25);
  if (p.err) return <ErrorBox msg={p.err} />;
  if (p.loading && p.items.length === 0) return <Loading />;
  if (p.items.length === 0) return <Empty msg="No transactions yet." />;
  return (
    <>
      <ul className="space-y-2">
        {p.items.map((t) => (
          <li key={t.id} className="card px-4 py-3">
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-navy">
                  {t.amount === null ? "Visit-only" : `₱${t.amount}`}{" "}
                  <StatusPill status={t.status} />
                </p>
                <p className="mt-0.5 text-xs text-ink-dark">
                  {new Date(t.createdAt).toLocaleString()}
                </p>
                {t.status === "VOIDED" && t.voidReason && (
                  <p className="mt-1 text-xs text-red-700">
                    Voided: {t.voidReason}
                  </p>
                )}
                {t.status === "REFUNDED" && t.refundReason && (
                  <p className="mt-1 text-xs text-amber-700">
                    Refunded ₱{t.refundedAmount}: {t.refundReason}
                  </p>
                )}
              </div>
              {t.loyaltyCreditSnapshot && (
                <div className="text-right text-xs">
                  {t.loyaltyCreditSnapshot.points !== undefined &&
                    t.loyaltyCreditSnapshot.points !== null && (
                      <p className="font-mono text-navy">
                        +{t.loyaltyCreditSnapshot.points.toFixed(2)} pts
                      </p>
                    )}
                  {t.loyaltyCreditSnapshot.stamps !== undefined &&
                    t.loyaltyCreditSnapshot.stamps !== null && (
                      <p className="font-mono text-navy">
                        +{t.loyaltyCreditSnapshot.stamps} stamps
                      </p>
                    )}
                  {(t.pointsReversed > 0 || t.stampsReversed > 0) && (
                    <p className="mt-0.5 font-mono text-red-700">
                      Reversed: {t.pointsReversed.toFixed(2)} pts /{" "}
                      {t.stampsReversed} stamps
                    </p>
                  )}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
      <PaginationControls {...p} />
    </>
  );
}

// ----- 2. Redemptions tab -----

export function CustomerRedemptionsTab({ tcId }: { tcId: string }) {
  const url = `/api/v1/tenant/customers/${tcId}/redemptions`;
  const p = usePaginated<CustomerRedemptionItem>(url, 25);
  if (p.err) return <ErrorBox msg={p.err} />;
  if (p.loading && p.items.length === 0) return <Loading />;
  if (p.items.length === 0)
    return <Empty msg="No vouchers issued or redeemed yet." />;
  return (
    <>
      <ul className="space-y-2">
        {p.items.map((r) => (
          <li key={r.id} className="card px-4 py-3">
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-navy">
                  {r.rewardName ?? "Reward"}{" "}
                  <StatusPill status={r.status} />
                </p>
                <p className="mt-0.5 text-xs text-ink-dark">
                  Code{" "}
                  <span className="font-mono">{r.voucherCode}</span> · issued{" "}
                  {new Date(r.issuedAt).toLocaleDateString()}
                </p>
                {r.redeemedAt && (
                  <p className="mt-0.5 text-xs text-emerald-700">
                    Redeemed {new Date(r.redeemedAt).toLocaleString()}
                  </p>
                )}
                {!r.redeemedAt && (
                  <p className="mt-0.5 text-xs text-ink-dark">
                    Expires {new Date(r.expiresAt).toLocaleDateString()}
                  </p>
                )}
              </div>
              <div className="text-right text-xs text-ink-dark">
                {r.pointsSpent !== null && (
                  <p className="font-mono">−{r.pointsSpent.toFixed(2)} pts</p>
                )}
                {r.stampsSpent !== null && (
                  <p className="font-mono">−{r.stampsSpent} stamps</p>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
      <PaginationControls {...p} />
    </>
  );
}

// ----- 3. Adjustments tab -----

export function CustomerAdjustmentsTab({ tcId }: { tcId: string }) {
  const url = `/api/v1/tenant/customers/${tcId}/adjustments`;
  const p = usePaginated<CustomerAdjustmentItem>(url, 25);
  if (p.err) return <ErrorBox msg={p.err} />;
  if (p.loading && p.items.length === 0) return <Loading />;
  if (p.items.length === 0) return <Empty msg="No manual adjustments." />;
  return (
    <>
      <ul className="space-y-2">
        {p.items.map((a) => {
          const sign = a.delta > 0 ? "+" : "";
          const tone =
            a.delta > 0 ? "text-emerald-700" : "text-red-700";
          const balance =
            a.currency === "POINTS"
              ? a.runningPointsBalance
              : a.runningStampsBalance;
          return (
            <li key={a.id} className="card px-4 py-3">
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <p className={`text-sm font-mono font-semibold ${tone}`}>
                    {sign}
                    {a.delta} {a.currency.toLowerCase()}
                  </p>
                  <p className="mt-0.5 text-xs text-ink-dark">{a.reason}</p>
                  <p className="mt-0.5 text-[10px] text-ink-dark/70">
                    by {a.actor} ·{" "}
                    {new Date(a.createdAt).toLocaleString()}
                  </p>
                </div>
                {balance !== null && (
                  <div className="text-right text-xs text-ink-dark">
                    <p>Balance after</p>
                    <p className="font-mono text-navy">{balance}</p>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <PaginationControls {...p} />
    </>
  );
}

// ----- 4. Consents tab -----

export function CustomerConsentsTab({ tcId }: { tcId: string }) {
  const url = `/api/v1/tenant/customers/${tcId}/consents`;
  const p = usePaginated<CustomerConsentItem>(url, 25);
  if (p.err) return <ErrorBox msg={p.err} />;
  if (p.loading && p.items.length === 0) return <Loading />;
  if (p.items.length === 0)
    return <Empty msg="No consent events recorded." />;
  return (
    <>
      <p className="mb-3 text-xs text-ink-dark">
        Only consent events tied to your tenant are shown — events
        from other merchants this customer may have joined are
        intentionally hidden.
      </p>
      <ul className="space-y-2">
        {p.items.map((c) => (
          <li key={c.id} className="card px-4 py-3">
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-navy">
                  {prettyConsent(c.consentType)}{" "}
                  <span
                    className={`ml-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                      c.granted
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-gray-200 text-gray-800"
                    }`}
                  >
                    {c.granted ? "Granted" : "Revoked"}
                  </span>
                </p>
                <p className="mt-0.5 text-xs text-ink-dark">
                  {new Date(c.grantedAt).toLocaleString()} · via{" "}
                  {c.grantedVia}
                  {c.documentVersion ? ` · v${c.documentVersion}` : ""}
                </p>
                {c.revokedAt && (
                  <p className="mt-0.5 text-xs text-red-700">
                    Revoked {new Date(c.revokedAt).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
      <PaginationControls {...p} />
    </>
  );
}

// ----- presentation helpers -----

function StatusPill({ status }: { status: string }) {
  const cls =
    status === "COMPLETED" || status === "REDEEMED"
      ? "bg-emerald-100 text-emerald-800"
      : status === "VOIDED"
        ? "bg-red-100 text-red-800"
        : status === "REFUNDED"
          ? "bg-amber-100 text-amber-800"
          : status === "ISSUED"
            ? "bg-blue-100 text-blue-800"
            : status === "EXPIRED"
              ? "bg-gray-200 text-gray-700"
              : "bg-gray-100 text-gray-800";
  return (
    <span
      className={`ml-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}
    >
      {status}
    </span>
  );
}

function Loading() {
  return <p className="text-sm text-ink-dark">Loading…</p>;
}

function Empty({ msg }: { msg: string }) {
  return (
    <p className="rounded-card border border-dashed border-navy/20 px-4 py-6 text-center text-sm text-ink-dark">
      {msg}
    </p>
  );
}

function ErrorBox({ msg }: { msg: string }) {
  return (
    <div className="rounded-card border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {msg}
    </div>
  );
}

function prettyConsent(t: string): string {
  // consent_type values are SCREAMING_SNAKE_CASE; render Title Case
  // for readability.
  return t
    .toLowerCase()
    .split("_")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
}

// Tiny utility: re-export the response types for parent-page typing
// without forcing a second @orbit/api-zod import path.
export type {
  TenantCustomerTransactionsResponse,
  TenantCustomerRedemptionsResponse,
  TenantCustomerAdjustmentsResponse,
  TenantCustomerConsentsResponse,
};
